import { useEffect, useId, useRef, useState } from 'react';
import { api, type WeekChange } from '../api.ts';
import { copy } from '../copy/en-CA.ts';
import { describeWeekChange, describeWeekChangesFailure } from '../describe.ts';
import { useSession } from '../session.tsx';
import { Notice } from './Notice.tsx';

const c = copy.weekChanges;

type State =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'shown'; changes: WeekChange[]; asOf: string; saving: boolean; error: string | null }
  | { kind: 'dismissed' }
  | { kind: 'failed'; message: string };

/**
 * "Your on-call weeks changed", at the top of a physician's home page
 * notices (owner, 24 October 2026): each week of a saved schedule an admin
 * moved to or from them since they last pressed "Got it". The rules for
 * what is listed are in src/lib/scheduling/week-changes.ts; the words for
 * each line in describeWeekChange.
 *
 * The live region is on the page from the start, empty, so the notice is
 * announced politely when it arrives (a region added together with its
 * content is not announced reliably). It has no role: it is not a status
 * message, and the page's other notices are. Empty, it is taken out of the
 * page's column (app.css) so it leaves no gap. Not printed.
 *
 * "Got it" closes it. Focus then moves to a line, read by a screen reader
 * and hidden on screen, where the notice was, so the person keeps their
 * place instead of being sent to the top of the page.
 */
export function WeekChangesNotice() {
  const { user } = useSession();
  const physicianId = user?.physicianId ?? null;
  const [state, setState] = useState<State>({ kind: 'loading' });
  const headingId = useId();
  const doneRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!physicianId) return;
    let cancelled = false;
    api
      .weekChanges()
      .then((r) => {
        if (!cancelled) setState(r.changes.length > 0 ? { kind: 'shown', changes: r.changes, asOf: r.asOf, saving: false, error: null } : { kind: 'none' });
      })
      .catch((err: unknown) => {
        // Said beside the schedule, which did load: the person should know the check did not happen.
        if (!cancelled) setState({ kind: 'failed', message: describeWeekChangesFailure(err, 'load') });
      });
    return () => {
      cancelled = true;
    };
  }, [physicianId]);

  useEffect(() => {
    if (state.kind === 'dismissed') doneRef.current?.focus();
  }, [state.kind]);

  if (!physicianId) return null;

  const gotIt = async () => {
    if (state.kind !== 'shown' || state.saving) return;
    setState({ ...state, saving: true, error: null });
    try {
      await api.weekChangesSeen(state.asOf);
      setState({ kind: 'dismissed' });
    } catch (err) {
      setState({ ...state, saving: false, error: describeWeekChangesFailure(err, 'seen') });
    }
  };

  return (
    <>
      <div className="no-print week-changes-live" aria-live="polite">
        {state.kind === 'shown' ? (
          <section className="notice info week-changes" aria-labelledby={headingId}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M16 3v4M8 3v4M3 11h18" />
              <path d="m10 15 2 2 2-2" />
            </svg>
            <div className="week-changes-body">
              <h2 id={headingId}>{c.title}</h2>
              <ul>
                {state.changes.map((change) => {
                  const line = describeWeekChange(change);
                  return (
                    <li key={`${change.scheduleId} ${change.weekStart}`}>
                      <span>
                        <strong>{line.range}</strong>: {line.what}
                      </span>{' '}
                      <span className="week-change-by">{line.by}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="week-changes-note">{c.note}</p>
              {state.error ? <Notice kind="error">{state.error}</Notice> : null}
              <div>
                <button type="button" className="btn primary" aria-disabled={state.saving} onClick={() => void gotIt()}>
                  {state.saving ? c.saving : c.gotIt}
                </button>
              </div>
            </div>
          </section>
        ) : null}
        {state.kind === 'failed' ? <Notice kind="error">{state.message}</Notice> : null}
      </div>
      {state.kind === 'dismissed' ? (
        <p className="visually-hidden no-print" tabIndex={-1} ref={doneRef}>
          {c.dismissed}
        </p>
      ) : null}
    </>
  );
}
